import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApi } from '../../../Services/apiService';
import { toast } from 'react-toastify';

const formatRelativeTime = (timestampMs) => {
  if (!timestampMs) return '';
  const now = Date.now();
  const diff = now - timestampMs;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}min.`;
  if (hours < 24) return `${hours}hr.`;
  if (days < 7) return `${days}d.`;
  return new Date(timestampMs).toLocaleDateString();
};

const Notification = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadingMoreRef = useRef(false);
  const sentinelRef = useRef(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    pageSize: 10,
  });
  // const [unreadOnly, setUnreadOnly] = useState(false);

  const fetchNotifications = async (page = 1, append = false) => {
    try {
      if (append) {
        loadingMoreRef.current = true;
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);
      const response = await getApi('/notifications', {
        params: { page, limit: 10, unreadOnly: false },
      });
      if (response?.statusCode === 200 && response?.data) {
        const list = response.data.data || [];
        setNotifications(append ? (prev) => [...prev, ...list] : list);
        setPagination({
          currentPage: response.data.currentPage ?? page,
          totalPages: response.data.totalPages ?? 1,
          totalItems: response.data.totalItems ?? 0,
          pageSize: response.data.pageSize ?? 10,
        });
      }
    } catch (err) {
      const errMsg = err?.response?.data?.message || 'Failed to load notifications.';
      setError(errMsg);
      if (!append) setNotifications([]);
      toast.error(errMsg);
    } finally {
      setLoading(false);
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchNotifications(1, false);
  }, []);

  const handleCardClick = (notification) => {
    if (notification?.link) {
      navigate(notification.link);
    }
  };

  const hasMore = pagination.currentPage < pagination.totalPages;

  // Infinite scroll: fetch the next page when the sentinel below the list scrolls into view.
  // Re-observing after each page loads re-checks visibility, so short pages keep loading until the view is filled.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || !hasMore || error) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMoreRef.current) {
          fetchNotifications(pagination.currentPage + 1, true);
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMore, error, pagination.currentPage, notifications.length]);

  if (loading && notifications.length === 0) {
    return (
      <div className="notification-page">
        <div className="notification-container">
          <h1 className="notification-title">Notifications</h1>
          <p style={{ color: '#666' }}>Loading notifications...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="notification-page">
      <div className="notification-container">
        <div style={{ marginBottom: 24 }}>
          <h1 className="notification-title">Notifications</h1>
          {/* Unread only filter
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 14, color: '#666' }}>
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(e) => setUnreadOnly(e.target.checked)}
            />
            Unread only
          </label>
          */}
        </div>
        {error && (
          <p style={{ color: '#c00', marginBottom: 16 }}>{error}</p>
        )}
        <div className="notification-list">
          {notifications.length === 0 ? (
            <p style={{ color: '#666' }}>No notifications yet.</p>
          ) : (
            notifications.map((notification) => (
              <div
                key={notification.id}
                className="notification-card"
                role={notification.link ? 'button' : undefined}
                onClick={() => handleCardClick(notification)}
                style={notification.link ? { cursor: 'pointer' } : undefined}
              >
                <div className="notification-header">
                  <h3 className="notification-card-title">{notification.title}</h3>
                  <div className="notification-meta">
                    <span className="notification-timestamp">
                      {formatRelativeTime(notification.createdAt)}
                    </span>
                    {!notification.is_read && (
                      <span className="notification-indicator" />
                    )}
                  </div>
                </div>
                <div
                  className="notification-description"
                  dangerouslySetInnerHTML={{
                    __html: notification.description || '',
                  }}
                />
              </div>
            ))
          )}
        </div>
        {hasMore && (
          <div ref={sentinelRef} style={{ height: 1 }} />
        )}
        {loadingMore && (
          <p style={{ color: '#666', textAlign: 'center', marginTop: 16 }}>Loading more...</p>
        )}
      </div>
    </div>
  );
};

export default Notification;
